const CONFIGURATION_FEATURES = {
  AI_EMAIL_BUNDLE_INGESTION: {
    name: 'ai_email_bundle_ingestion',
    display_name: 'Clive - Email Bundle Ingestion',
    description:
      "Sends a new email to Clive as one bundle: a single /store/bundle call carrying the email and, when the AI configuration processes email attachments, each attachment as its child, instead of one insert per document. An attachment Clive already holds (an attached claim document, or a forwarded attachment) is sent by the id Clive knows it by, so Clive links it to the email and does not read it again. Clive keeps every bundled attachment on the claim. CMS still creates the claim document for a related attachment, with no second attach call, and detaches an attachment Clive judges not insurance related. The email cannot be detached from its claim while an attachment is still being processed. An email that already has a Clive store record (detached and re-attached, or attached from the general queue) keeps the per-document attach path. Turning it off sends new emails the old way; bundles already sent are still handled. Do not turn it on for an organization before Clive's bundle backstop sweep and late-child handling are deployed.",
    category: 'Short-term Rollout',
    enabled_by_default: false,
    editable_by_support: false,
    enabled_by_default_for_demo: false,
  },
  CLAIM_MILESTONES: {
    name: 'claim_milestones',
    display_name: 'Claim Milestones',
    description:
      "Org-configured claim milestones: the dates of key events on a claim, such as when the insured was first contacted, recorded against milestone definitions each organization configures. Short-lived rollout flag, removed at GA. It gates the definition configuration writes (create, update, reorder, and the Configuration Repository import and promotion), the ClaimMilestoneDefinitions table of scheduled and one-time export files, the milestones the external Claims API lists on a claim, which are empty while it is off, and recording or clearing a claim's milestone dates, through the external Claims API or the claim page's own routes, which is refused while it is off; reading the definitions or a claim's dates through the claim page's own routes, the Configuration Repository export and the Metabase table are not gated. It also shows the Milestones tab in the claim page side bar and the tab's entry in the side-bar ordering screen. It also gates the Conductor's record_claim_milestone claim action, on discovery and on execution, and the claim's milestones line in the AI claim context. It also adds the Claim milestones standing duty to the conductor's general instructions, under Standing duties, wherever the action is enabled and the scope has an enabled milestone; the Conductor configuration screen does not show that text, so it is not to be authored there by hand. See docs/design-reviews/claim-milestones-dr.md.",
    category: 'Short-term Rollout',
    enabled_by_default: false,
    editable_by_support: true,
    enabled_by_default_for_demo: false,
  },
  CONFIGURATION_MCP: {
    name: 'configuration_mcp',
    display_name: 'Configuration MCP',
    description:
      "Enables the Configuration MCP endpoint (POST /mcp/config), a Five Sigma staff tool: an MCP client such as Claude Code, connected through CMS's OAuth authorization server (the user signs in to CMS and approves the client, which only super-organization users may do), can browse configuration types and the configuration of any organization, draft changes that land as a pending in-environment promotion for a human to review and approve in the Promotions tab, report a promotion's status, and withdraw a pending promotion the user drafted through it. The endpoint never approves. Every tool refuses a token whose user is not a super-organization user, except a Configuration Assistant token CMS issues to the AI service bound to the user's own organization (see CONFIGURATION_ASSISTANT, which gates those tokens instead of this flag). The flag is read on the token user's own organization (the super organization for staff), not on the organization a tool targets, so turning it on for the super organization switches the endpoint on in that deployment. While it is off, /mcp/config answers 401 to that user's tokens; the OAuth endpoints and the consent screen do not read the flag, so a client can still connect, but /mcp/config refuses its tokens.",
    category: 'Short-term Rollout',
    enabled_by_default: false,
    editable_by_support: false,
    enabled_by_default_for_demo: false,
  },
  CONFIGURATION_ASSISTANT: {
    name: 'configuration_assistant',
    display_name: 'Configuration Assistant',
    description:
      "Shows the Configuration Assistant tab in the admin panel's System Configuration → Configuration Management for this organization, to Five Sigma staff configuring it, and gates its routes (/sysconfig/api/v1/organizations/<id>/agents/configuration_assistant/...), which refuse any user who is not Five Sigma staff: the organization's own users do not see the tab. Each message is answered by the AI service's agent through the Configuration MCP with a short-lived access token CMS mints for the signed-in user and binds to this organization: the MCP refuses any other organization for it. The agent drafts a change and creates a pending promotion, which a person approves in the Promotions tab; it never approves. The flag is read on the configured organization, for the routes and for the MCP's check of such a token; CONFIGURATION_MCP, which external MCP clients need, is not read for it.",
    category: 'Short-term Rollout',
    enabled_by_default: false,
    editable_by_support: false,
    enabled_by_default_for_demo: false,
  },
  SYSTEM_OF_RECORD: {
    name: 'system_of_record',
    display_name: 'System of Record',
    description:
      "Rollout flag for committing claim actions at the organization's external System of Record (SoR) before CMS commits them. While it is off no operation requires the SoR, whatever the organization's System of Record configuration says. The sysconfig configuration routes are deliberately not gated, so an organization can be configured ahead of the flip. Turning it off also stops the reconciler asking the SoR about the changes still unconfirmed: it hands them to an operator on the sysconfig Issues routes, which are not gated either. It surfaces the SOR_ISSUES_MANAGE permission in the permissions screen, with the caveat every flag-gated permission shares: while the flag is off the permission is hidden from the profile form, so the next profile save drops it.",
    category: 'Short-term Rollout',
    enabled_by_default: false,
    editable_by_support: false,
    enabled_by_default_for_demo: false,
  },
  DOCUMENT_REQUESTS: {
    name: 'document_requests',
    display_name: 'Document Requests',
    description:
      "Enables raising a document request on a claim: a single-use link a claimant opens to upload documents straight to storage, without a CMS login. Gates the claim action that raises a request, the two communication tokens that embed one in an email, the org branding card under System Configuration, and the review/attach affordances on the claim's Documents tab. The inbound webhook that records a submission is not gated: a request raised while the flag was on must still be able to deliver its files after the flag is turned off. Turning it off stops new requests; requests already sent stay valid until they expire. See docs/design-reviews/document-request-dr.md.",
    category: 'Short-term Rollout',
    enabled_by_default: false,
    editable_by_support: false,
    enabled_by_default_for_demo: false,
  },
  RECOVERY_REQUESTS: {
    name: 'recovery_requests',
    display_name: 'Recovery Requests',
    description:
      'Enables asking a payer to send money back on a claim. Gates request creation and surfaces the RECOVERY_REQUEST permission in the permissions screen; the sysconfig configuration routes are deliberately not gated, so an organization can be configured ahead of the flip. Turning it off stops new requests while a request already sent stays listed and money arriving against it still settles. One caveat, shared with every flag-gated permission: while the flag is off the permission is hidden from the profile form, so the next profile save drops the RECOVERY_REQUEST verb and withdrawing an in-flight request needs the flag back on. See docs/design-reviews/one-inc-inbound-payments-dr.md.',
    category: 'Short-term Rollout',
    enabled_by_default: false,
    editable_by_support: false,
    enabled_by_default_for_demo: false,
  },
  CUSTOM_FNOL_ADDITIONAL_INFORMATION_SECTIONS: {
    name: 'custom_fnol_additional_information_sections',
    display_name: 'Custom FNOL Additional Information Sections',
    description:
      'Lets admins create custom supplementary "Additional Information" sections on FNOL configurations of any LOB (on auto they follow the four built-in sections). Gates only the authoring affordance (the "Add Section" button/dialog); rendering of already-created sections on the FNOL form and claim page is data-driven and unaffected by the flag.',
    category: 'Short-term Rollout',
    enabled_by_default: false,
    editable_by_support: false,
    enabled_by_default_for_demo: false,
  },
  FUND_ACCOUNT_ENGINE: {
    name: 'fund_account_engine',
    display_name: 'Fund Account Engine',
    description:
      'Enables the Fund Account Engine (rule-based resolution of the fund/bank account a payment is sent from). See docs/design-reviews/fund-account-engine-dr.md.',
    category: 'Short-term Rollout',
    enabled_by_default: false,
    editable_by_support: false,
    enabled_by_default_for_demo: false,
  },
  INCIDENT_CONFIGURATION_DRAFTS: {
    name: 'incident_configuration_drafts',
    display_name: 'Incident Configuration Drafts',
    description:
      'Enables saving a FNOL/incident configuration as an unversioned draft from the configuration screen before publishing it as a new version.',
    category: 'Short-term Rollout',
    enabled_by_default: false,
    editable_by_support: true,
    enabled_by_default_for_demo: false,
  },
  NOTIFICATION_RULES_QUERY_BUILDER: {
    name: 'notification_rules_query_builder',
    display_name: 'Notification Rules Query Builder',
    description:
      "Offers the shared QueryBuilder conditions editor when authoring a notification rule, persisting the tree as conditions_json/conditions_cel, and makes CEL authoritative when the rule evaluates. Off: rules can only be authored with the legacy condition rows and every rule is decided by the existing condition walk, with CEL evaluated in the shadow for any rule that already holds a tree so a divergence is reported. Stays off for an organization until its rules have been fully migrated to conditions_json and cleared parity. Switching it back off after rules were authored in the QueryBuilder needs those rules' conditions cleared first: they hold no legacy condition rows, so the condition walk would match everything in their scope.",
    category: 'Short-term Rollout',
    enabled_by_default: false,
    editable_by_support: false,
    enabled_by_default_for_demo: false,
  },
  EFFICIENT_EXPOSURES_QUERY: {
    name: 'efficient_exposures_query',
    display_name: 'Efficient Exposures Query',
    description:
      'Load the claim exposures screen with a split loader strategy: the constant-per-request claim tree and the row-multiplying methods-of-inspection collection are fetched via separate SELECT-IN queries instead of being joined into the main exposures statement. Behaviour-identical to the legacy joined query; off by default during rollout.',
    category: 'Short-term Rollout',
    enabled_by_default: false,
    editable_by_support: false,
    enabled_by_default_for_demo: false,
  },
  CLAIM_ACTIONS_CONFIGURED_EXECUTION: {
    name: 'claim_actions_configured_execution',
    display_name: 'Claim Actions Configured Execution',
    description:
      'Execute duplicated claim actions (e.g. coverage decision) through the new claim action configuration registry tool path instead of the legacy execute path.',
    category: 'Short-term Rollout',
    enabled_by_default: false,
    editable_by_support: true,
    enabled_by_default_for_demo: false,
  },
  ADDITIONAL_FIELDS_ON_CLAIM_BAR: {
    name: 'additional_fields_on_claim_bar',
    display_name: 'Additional Fields on Claim Bar',
    description:
      'Lets an organization add fields to the claim bar and configure it per scope (each line of business, or each sub-organization and line of business when the organization uses sub-organizations). When off, every claim shows the Default claim bar; scoped configurations stay stored and return when it is turned on again. Removed at GA.',
    category: 'Short-term Rollout',
    enabled_by_default: false,
    editable_by_support: true,
    enabled_by_default_for_demo: false,
  },
  MAKE_CLAIM_EVENTS_WEBHOOKS_WORK_WITHOUT_CLAIM: {
    name: 'make_claim_events_webhooks_work_without_claim',
    display_name: 'Make Claim Events Webhooks Work Without a Claim',
    description: 'Make Claim Events Webhooks Work Without a Claim',
    category: 'Short-term Rollout',
    enabled_by_default: true,
    editable_by_support: true,
    enabled_by_default_for_demo: true,
  },
  RESERVE_CHANGE_REQUEST_WARNINGS: {
    name: 'reserve_change_request_warnings',
    display_name: 'Reserve Change Request Warnings',
    description: 'Reserve Change Request Warnings',
    category: 'Short-term Rollout',
    enabled_by_default: false,
    editable_by_support: false,
    enabled_by_default_for_demo: false,
  },
  EMAIL_IMAGE_PROXY_ENABLED: {
    name: 'email_image_proxy_enabled',
    display_name: 'Email Image Proxy (POC)',
    description:
      "POC (NGTPA-24975): rewrite <img src=...> in email body_html to route through an HMAC-signing image proxy (go-camo). Renders external/incoming-mail images without leaking the recipient's IP to the sender, and lets us allow img tags in incoming bleach sanitization. Disabled by default while the proxy is local-only.",
    category: 'Short-term Rollout',
    enabled_by_default: false,
    editable_by_support: false,
    enabled_by_default_for_demo: false,
  },
  CONFIGURATION_MANAGEMENT: {
    name: 'configuration_management',
    display_name: 'Configuration Management',
    description: 'Enables the Configuration Management tab with the Snapshots & Backup and Promotions sub-tabs.',
    category: 'Short-term Rollout',
    enabled_by_default: false,
    editable_by_support: false,
    enabled_by_default_for_demo: false,
  },
  STARR_DEMO_AUDIT_LOG: {
    name: 'starr_demo_audit_log',
    display_name: 'Starr Demo Audit Log',
    description: 'Enables the Audit Log and Change Requests tabs within Configuration Management',
    category: 'Short-term Rollout',
    enabled_by_default: false,
    editable_by_support: false,
    enabled_by_default_for_demo: false,
  },
  TEST_FF: {
    name: 'test_ff',
    display_name: 'Test FF',
    description: 'FF used by the automatic tests to test the feature flag system',
    category: 'Short-term Rollout',
    enabled_by_default: false,
    editable_by_support: false,
    enabled_by_default_for_demo: false,
  },
  NOTIFICATION_PERCENTAGE_CONDITION_LIMIT_PER_INCIDENT: {
    name: 'limit_type_per_incident',
    display_name: 'Limit Type Per Incident for Percentage Conditions in Notifications',
    description: 'Enables configuring limit type per incident for percentage conditions in notifications',
    category: 'Short-term Rollout',
    enabled_by_default: false,
    editable_by_support: false,
    enabled_by_default_for_demo: false,
  },
  NOTIFICATION_PERCENTAGE_CONDITION_LIMIT_PER_POLICY: {
    name: 'limit_type_per_policy',
    display_name: 'Limit Type Per Policy for Percentage Conditions in Notifications',
    description: 'Enables configuring limit type per policy for percentage conditions in notifications',
    category: 'Short-term Rollout',
    enabled_by_default: false,
    editable_by_support: false,
    enabled_by_default_for_demo: false,
  },
  NOTIFICATION_PERCENTAGE_CONDITION_LIMIT_PER_PERSON: {
    name: 'limit_type_per_person',
    display_name: 'Limit Type Per Person for Percentage Conditions in Notifications',
    description: 'Enables configuring limit type per person for percentage conditions in notifications',
    category: 'Short-term Rollout',
    enabled_by_default: false,
    editable_by_support: false,
    enabled_by_default_for_demo: false,
  },
  NOTIFICATION_KANBAN_BOARD: {
    name: 'notification_kanban_board',
    display_name: 'Notification Kanban Board',
    description: 'Notification Kanban Board',
    category: 'Short-term Rollout',
    enabled_by_default: false,
    editable_by_support: false,
    enabled_by_default_for_demo: false,
  },
  COMMERCIAL_POLICIES: {
    name: 'commercial_policies',
    display_name: 'Commercial Policies',
    description: 'Commercial Policies',
    category: 'Short-term Rollout',
    enabled_by_default: false,
    editable_by_support: false,
    enabled_by_default_for_demo: false,
  },
  TABLEAU_ACCOUNTS: {
    name: 'tableau_accounts',
    display_name: 'Tableau Accounts',
    description: 'Enables Tableau Accounts Feature',
    category: 'Feature',
    enabled_by_default: false,
    editable_by_support: false,
    enabled_by_default_for_demo: false,
  },
  SHOW_RELATED_CLAIMS_TAB: {
    name: 'show_related_claims_tab',
    display_name: 'Show Related Claims Tab',
    description:
      'Enables the Related Claims tab in claim detail pages that shows claims sharing the same covered entity',
    category: 'Feature',
    enabled_by_default: false,
    editable_by_support: true,
    enabled_by_default_for_demo: false,
  },
  ENABLE_MARK_AS_UW: {
    name: 'enable_mark_as_uw',
    display_name: 'Mark Claim as UW',
    description: 'Adds option to mark claim as UW',
    category: 'Feature',
    enabled_by_default: false,
    editable_by_support: false,
    enabled_by_default_for_demo: false,
  },
  ENABLE_MARK_AS_CONTROL_FILE: {
    name: 'enable_mark_as_control_file',
    display_name: 'Mark Claim as Control File',
    description: 'Adds option to mark claim as Control File',
    category: 'Feature',
    enabled_by_default: false,
    editable_by_support: false,
    enabled_by_default_for_demo: false,
  },
  ENABLE_MARK_AS_DEFERRED: {
    name: 'enable_mark_as_deferred',
    display_name: 'Mark Claim as Deferred',
    description: 'Adds option to mark claim as Deferred',
    category: 'Feature',
    enabled_by_default: false,
    editable_by_support: false,
    enabled_by_default_for_demo: false,
  },
  SUB_RESERVERS_IN_PD_AND_BI: {
    name: 'sub_reserves_in_pd_and_bi',
    display_name: 'Show Reserve Sub Calculations',
    description: 'Exposes calculation of reserve only for BI & PD exposures',
    category: 'Feature',
    enabled_by_default: false,
    editable_by_support: false,
    enabled_by_default_for_demo: false,
  },
  ASSIGNMENTS_CONFIGURATION: {
    name: 'assignments_configuration',
    display_name: 'Assignment Configuration',
    description: 'Enables assignment rules configuration screen in admin panel',
    category: 'Short-term Rollout',
    enabled_by_default: true,
    editable_by_support: false,
    enabled_by_default_for_demo: true,
  },
  POLICIES_SEARCH_PAGE: {
    name: 'policies_search_page',
    display_name: 'Policies Search Page',
    description: 'Enables policy search tab',
    category: 'Feature',
    enabled_by_default: false,
    editable_by_support: false,
    enabled_by_default_for_demo: false,
  },
  SCHEDULED_DATA_EXPORT: {
    name: 'scheduled_data_export',
    display_name: 'Scheduled Data Export',
    description: 'Enables scheduling data exports',
    category: 'Short-term Rollout',
    enabled_by_default: true,
    editable_by_support: false,
    enabled_by_default_for_demo: true,
  },
  FF_TABLEAU_REPORTS: {
    name: 'ff_tableau_reports',
    display_name: 'Tableau Reports',
    description: 'enables tableau reports',
    category: 'Short-term Rollout',
    enabled_by_default: false,
    editable_by_support: false,
    enabled_by_default_for_demo: true,
  },
  CONFIGURABLE_COVERAGES: {
    name: 'configurable_coverages',
    display_name: 'Configurable Coverages',
    description: 'Enables configurable coverages',
    category: 'Long-term Rollout',
    enabled_by_default: true,
    editable_by_support: false,
    enabled_by_default_for_demo: true,
  },
  CONFIGURABLE_FNOL: {
    name: 'configurable_fnol',
    display_name: 'Configurable FNOL',
    description: 'Enables configuring FNOL and incident fields',
    category: 'Long-term Rollout',
    enabled_by_default: true,
    editable_by_support: false,
    enabled_by_default_for_demo: true,
  },
  GENERIC_POLICY_PAGE_V2: {
    name: 'generic_policy_page_v2',
    display_name: 'Generic Policy Page V2',
    description:
      'Renders the Policy tab as two tabs (Details + Covered Objects) with paginated per-type tables and server-side search. Requires CONFIGURABLE_FNOL.',
    category: 'Long-term Rollout',
    enabled_by_default: false,
    editable_by_support: true,
    enabled_by_default_for_demo: false,
  },
  PET_DEDUCTIBLE_UI: {
    name: 'pet_deductible_ui',
    display_name: 'Pet deductible UI',
    description: 'Add to UI deductible related additions',
    category: 'Feature',
    enabled_by_default: true,
    editable_by_support: false,
    enabled_by_default_for_demo: true,
  },
  AUTO_USE_GC_DEDUCTIBLE_FLOW: {
    name: 'auto_use_gc_deductible_flow',
    display_name: 'Auto claims use GC deductible flow',
    description:
      'Auto claims route to the same GcClaimDeductibleOperations as General Claims: adjuster-editable amount seeded from policy, retroactive apply, per-payment cancel',
    category: 'Feature',
    enabled_by_default: false,
    editable_by_support: false,
    enabled_by_default_for_demo: false,
  },
  ONLINE_EDIT: {
    name: 'online_edit',
    display_name: 'Allow online edit of document templates',
    description: 'Integration with third party online editor to edit document templates via the browser',
    category: 'Feature',
    enabled_by_default: false,
    editable_by_support: false,
    enabled_by_default_for_demo: false,
  },
  PERMISSIONS_ENFORCEMENT: {
    name: 'permissions_enforcement',
    display_name: 'Permissions Enforcement',
    description: 'Enables permissions enforcement',
    category: 'Long-term Rollout',
    enabled_by_default: true,
    editable_by_support: false,
    enabled_by_default_for_demo: true,
  },
  AUTOMATIC_EXPOSURES_RULES: {
    name: 'automatic_exposures_rules',
    display_name: 'Automatic Exposures Rules',
    description: 'Configure automatic exposure rules',
    category: 'Short-term Rollout',
    enabled_by_default: true,
    editable_by_support: false,
    enabled_by_default_for_demo: true,
  },
  LOSS_REPORT_TEMP: {
    name: 'loss_report_temp',
    display_name: 'Loss Report Temp not GAed',
    description: 'Add loss report page - mainly for Inshur US',
    category: 'Feature',
    enabled_by_default: false,
    editable_by_support: false,
    enabled_by_default_for_demo: false,
  },
  EXTERNAL_DEV_TOOLS: {
    name: 'external_developer_tools',
    display_name: 'External Developer Tools',
    description: 'External Developer Tools',
    category: 'Short-term Rollout',
    enabled_by_default: false,
    editable_by_support: false,
    enabled_by_default_for_demo: false,
  },
  NEW_CLOSING_REASON: {
    name: 'new_closing_reason',
    display_name: 'Enable using exposure closing reason configuration',
    description: 'Instead of using hardcoded closing reasons enables to configure specific exposure closing reasons',
    category: 'Short-term Rollout',
    enabled_by_default: true,
    editable_by_support: false,
    enabled_by_default_for_demo: true,
  },
  ORG_HIERARCHY_CONFIGURATION: {
    name: 'org_hierarchy_configuration',
    display_name: 'Organizational units configuration interface',
    description: 'Enable organizational units configuration interface',
    category: 'Short-term Rollout',
    enabled_by_default: true,
    editable_by_support: false,
    enabled_by_default_for_demo: true,
  },
  ORG_HIERARCHY_WORKFLOWS: {
    name: 'org_hierarchy_workflows',
    display_name: 'Business workflows to utilize organizational units',
    description: 'Enable business workflows to utilize organizational units',
    category: 'Short-term Rollout',
    enabled_by_default: true,
    editable_by_support: false,
    enabled_by_default_for_demo: true,
  },
  LEGAL_ACTIONS: {
    name: 'legal_actions',
    display_name: 'Legal Actions Management',
    description: 'Access the legal actions management screen',
    category: 'Short-term Rollout',
    enabled_by_default: false,
    editable_by_support: false,
    enabled_by_default_for_demo: false,
  },
  MOI_EMAIL_TEMPLATES: {
    name: 'moi_email_templates',
    display_name: 'MOI Email Templates',
    description: 'Enable MOI Email Templates feature',
    category: 'Long-term Rollout',
    enabled_by_default: false,
    editable_by_support: false,
    enabled_by_default_for_demo: false,
  },
  MOI_ASSIGNMENT_RULES: {
    name: 'moi_assignment_rules',
    display_name: 'MOI Assignment Rules',
    description:
      'Show the MOI assignment rules tab in System Configuration and the Automatic rules assignee option in the MOI stepper',
    category: 'Short-term Rollout',
    enabled_by_default: false,
    editable_by_support: false,
    enabled_by_default_for_demo: false,
  },
  FF_TABLEAU_SELF_SERVICE: {
    name: 'ff_tableau_self_service',
    display_name: 'Tableau Self Service',
    description: 'Enable the reports and dashboards v2 with the self-service option',
    category: 'Short-term Rollout',
    enabled_by_default: false,
    editable_by_support: false,
    enabled_by_default_for_demo: false,
  },
  AI_EXPERTS_QA: {
    name: 'ai_experts_qa',
    display_name: 'Clive - AI Experts QA',
    description: 'Enable per-scope QA expert prompt and structured-output overrides',
    category: 'Short-term Rollout',
    enabled_by_default: false,
    editable_by_support: false,
    enabled_by_default_for_demo: false,
  },
  CLAIM_ACTIONS: {
    name: 'claim_actions',
    display_name: 'Clive - Claim Actions',
    description: 'Enable the Actions section of the Agent Configuration tab in Clive Configuration',
    category: 'Short-term Rollout',
    enabled_by_default: false,
    editable_by_support: false,
    enabled_by_default_for_demo: false,
  },
  AGENT_CONFIGURATION: {
    name: 'agent_configuration',
    display_name: 'Clive - Agent Configuration',
    description:
      'Show the Agent Configuration sub-tab in Clive Configuration, where an operator configures the conductor per sub-organization and line of business: the on/off switch, the general instructions, and a goal and instructions per claim stage. With the flag off the tab is hidden and its endpoints reject, and the conductor keeps serving whatever is already stored - the flag gates the screen, never how the configuration is read.',
    category: 'Short-term Rollout',
    enabled_by_default: false,
    editable_by_support: false,
    enabled_by_default_for_demo: false,
  },
  EXTERNAL_TOOL_APIS: {
    name: 'external_tool_apis',
    display_name: 'Clive - External Tool APIs',
    description:
      'Show the External Tool APIs sub-tab in Clive Configuration, where an operator points a Clive tool at their own external endpoint, and let configured integrations take effect: with the flag off no external override is resolved, so every tool keeps its CMS-internal endpoint. Per configuration, the enabled toggle still applies on top of this flag.',
    category: 'Short-term Rollout',
    enabled_by_default: false,
    editable_by_support: false,
    enabled_by_default_for_demo: false,
  },
  AI_SERVICE_UI_INSIGHTS_TAB_DISPLAY: {
    name: 'ai_service_insights_display',
    display_name: 'Clive - AI-Service Insights tab display',
    description: 'Enables Clive Insights tab display (demo)',
    category: 'Long-term Rollout',
    enabled_by_default: false,
    editable_by_support: true,
    enabled_by_default_for_demo: true,
  },
  AI_SERVICE_ACTIONS_INVOICE_ANALYSIS_DISPLAY: {
    name: 'ai_service_actions_invoice_analysis_display',
    display_name: 'Clive - AI-Service Invoice Analysis Action display',
    description: 'Enables Clive Invoice Analysis Action in Actions tab',
    category: 'Long-term Rollout',
    enabled_by_default: false,
    editable_by_support: false,
    enabled_by_default_for_demo: true,
  },
  AI_SERVICE_ACTIONS_DISPLAY_NO_INVOICE_ANALYSIS: {
    name: 'ai_service_actions_display_no_invoice_analysis',
    display_name: 'Clive - AI-Service Actions display no invoice analysis',
    description: 'Enables Clive Actions in Actions tab no invoice analysis',
    category: 'Feature',
    enabled_by_default: false,
    editable_by_support: false,
    enabled_by_default_for_demo: false,
  },
  AI_SERVICE_ACTIONS_DEMO_DISPLAY: {
    name: 'ai_service_actions_demo_display',
    display_name: 'Clive - AI-Service Demo Action display',
    description: 'Enables Clive Demo Action in Actions tab (demo)',
    category: 'Long-term Rollout',
    enabled_by_default: false,
    editable_by_support: true,
    enabled_by_default_for_demo: true,
  },
  API_BETA_ROUTES: {
    name: 'api_beta_routes',
    display_name: 'Allow external api beta routes',
    description: 'Allow external api beta routes',
    category: 'Feature',
    enabled_by_default: false,
    editable_by_support: true,
    enabled_by_default_for_demo: true,
  },
  PERFORMANCE_PERSIST_CLAIM_TABS: {
    name: 'persist_claim_tabs',
    display_name: 'Performance Persist Tabs in Claim Page',
    description: 'Persist tabs in claim page',
    category: 'Short-term Rollout',
    enabled_by_default: true,
    editable_by_support: true,
    enabled_by_default_for_demo: true,
  },
  MULTI_COUNTRY_ORGANIZATION: {
    name: 'multi_country_organization',
    display_name: 'Multi Country Organization',
    description: 'Enable Organization to have multiple countries',
    category: 'Feature',
    enabled_by_default: false,
    editable_by_support: false,
    enabled_by_default_for_demo: false,
  },
  AI_FNOL_INBOX_BDX_OVERRIDE: {
    name: 'ai_fnol_inbox_bdx_override',
    display_name: 'Clive - AI FNOL Inbox BDX Override (Demo)',
    description: 'Enable AI FNOL Inbox BDX override for organizations',
    category: 'Feature',
    enabled_by_default: false,
    editable_by_support: false,
    enabled_by_default_for_demo: false,
  },
  AI_INVOICE_ODIE_SPECIFIC_CALCULATION: {
    name: 'ai_invoice_odie_specific_calculation',
    display_name: 'Clive - AI Invoice Odie Specific Calculation',
    description: 'Enable AI Invoice Odie specific calculation that uses co_insurance field',
    category: 'Feature',
    enabled_by_default: false,
    editable_by_support: false,
    enabled_by_default_for_demo: false,
  },
  CLIVE_INVOICE_ACTION_NOTIFICATIONS: {
    name: 'clive_invoice_action_notifications',
    display_name: 'Clive - Invoice Action Notifications',
    description: 'Enable notifications for Clive invoice analysis actions',
    category: 'Short-term Rollout',
    enabled_by_default: false,
    editable_by_support: false,
    enabled_by_default_for_demo: false,
  },
  INVOICE_ALWAYS_SHOW_CO_INSURANCE: {
    name: 'invoice_always_show_co_insurance',
    display_name: 'Invoice Always Show Co-Insurance',
    description: 'Always show co-insurance data in invoice analysis',
    category: 'Feature',
    enabled_by_default: false,
    editable_by_support: true,
    enabled_by_default_for_demo: false,
  },
  INVOICE_QUICK_PAYMENT_IGNORE_RESERVE: {
    name: 'invoice_quick_payment_ignore_reserve',
    display_name: 'Invoice Quick Payment Ignore Reserve',
    description: 'Enable Invoice Quick Payment Ignore Reserve',
    category: 'Feature',
    enabled_by_default: false,
    editable_by_support: true,
    enabled_by_default_for_demo: false,
  },
  ALWAYS_SHOW_INVOICE_COVERAGE_BREAKDOWN: {
    name: 'always_show_invoice_coverage_breakdown',
    display_name: 'Always Show Invoice Coverage Breakdown',
    description: 'Enable Always Show Invoice Coverage Breakdown',
    category: 'Feature',
    enabled_by_default: false,
    editable_by_support: true,
    enabled_by_default_for_demo: false,
  },
  REINSURANCE_MANAGEMENT_TAB: {
    name: 'reinsurance_management_tab',
    display_name: 'Reinsurance Management Tab',
    description: 'Enable Reinsurance Management Tab Option',
    category: 'Short-term Rollout',
    enabled_by_default: false,
    editable_by_support: false,
    enabled_by_default_for_demo: false,
  },
  CLIVE_GQ_FNOL_CATEGORY_CLASSIFICATION_DISPLAY: {
    name: 'clive_gq_fnol_category_classification_display',
    display_name: 'Clive - GQ FNOL Category Classification Display',
    description: 'Shows the explanation for the fnol category, and the tag in the emails table (demo)',
    category: 'Short-term Rollout',
    enabled_by_default: false,
    editable_by_support: false,
    enabled_by_default_for_demo: false,
  },
  AI_SERVICE_INSIGHTS_CONTRACT_ANALYSIS_DISPLAY: {
    name: 'ai_service_insights_contract_analysis_display',
    display_name: 'Clive - AI-Service Contract Analysis Insights display (demo)',
    description: 'Enables Clive Contract Analysis Insights in Insights tab (demo)',
    category: 'Feature',
    enabled_by_default: false,
    editable_by_support: true,
    enabled_by_default_for_demo: true,
  },
  CLIVE_EMAIL_SUMMARY: {
    name: 'clive_email_summary',
    display_name: 'Clive Email Summary',
    description: 'Enable Clive Email Summary for email communications',
    category: 'Short-term Rollout',
    enabled_by_default: true,
    editable_by_support: false,
    enabled_by_default_for_demo: false,
  },
  CLIVE_APP_CLAIM_PAGE: {
    name: 'clive_app_claim_page',
    display_name: 'Clive App Claim Page',
    description: 'Enable Clive App Claim Page',
    category: 'Feature',
    enabled_by_default: false,
    editable_by_support: false,
    enabled_by_default_for_demo: false,
  },
  CLIVE_20_SECONDS_FNOL_DEMO: {
    name: 'clive_20_seconds_fnol_demo',
    display_name: 'Demo feature: Clive 20 Seconds FNOL Demo',
    description:
      "FNOL emails whose body contains a trigger from the GCS demo config (mock_demo/config.json) get that demo's canned FNOL after a short pause instead of AI processing. Also needs the AI configuration's demo FNOL email processing on; never runs in production",
    category: 'Feature',
    enabled_by_default: false,
    editable_by_support: false,
    enabled_by_default_for_demo: false,
  },
  CONDUCTOR_HUB_CLAIM_TAB: {
    name: 'conductor_hub_claim_tab',
    display_name: 'Automation First Claim Tab',
    description: 'Shows the Automation First tab, with its log and activity panels, in the claim sidebar.',
    category: 'Short-term Rollout',
    enabled_by_default: false,
    editable_by_support: false,
    enabled_by_default_for_demo: false,
  },
  CONDUCTOR_EVIDENCE_REFS: {
    name: 'conductor_evidence_refs',
    display_name: 'Conductor evidence refs',
    description:
      "Shows the Conductor's citations of expert reports in claim actions as chips with a preview and the full report. Off: those citations are not shown.",
    category: 'Short-term Rollout',
    enabled_by_default: false,
    editable_by_support: false,
    enabled_by_default_for_demo: false,
  },
  METABASE_APPROVED: {
    name: 'metabase_approved',
    display_name: 'Metabase Approved (hide Tableau)',
    description:
      'Org has fully migrated to Metabase. Hides all Tableau UI and Tableau-related feature flags. Reporting page is served at /reports instead of /reports_v3.',
    category: 'Feature',
    enabled_by_default: false,
    editable_by_support: true,
    enabled_by_default_for_demo: false,
  },
  TIME_BASED_NOTIFICATIONS: {
    name: 'time_based_notifications',
    display_name: 'Time-Based Notifications',
    description:
      'Enables creating notification rules that trigger based on time conditions (e.g., no notes for X days)',
    category: 'Short-term Rollout',
    enabled_by_default: false,
    editable_by_support: true,
    enabled_by_default_for_demo: false,
  },
  CLIVE_DEEP_ANALYSIS_STATUS: {
    name: 'clive_deep_analysis_status',
    display_name: 'Clive - Deep Analysis Status',
    description: "Enables showing the 'Analyzing' status on documents when deep analysis is pending",
    category: 'Short-term Rollout',
    enabled_by_default: false,
    editable_by_support: true,
    enabled_by_default_for_demo: false,
  },
  CALENDAR_ENHANCED_FILTERS: {
    name: 'calendar_enhanced_filters',
    display_name: 'Calendar - Enhanced Filters',
    description: 'Enables priority color coding and communication filtering toggle on the calendar',
    category: 'Short-term Rollout',
    enabled_by_default: false,
    editable_by_support: true,
    enabled_by_default_for_demo: true,
  },
  DAMAGE_ASSESSMENT_TYPE_CONFIG: {
    name: 'damage_assessment_type_config',
    display_name: 'Damage Assessment Type Configuration',
    description: 'Enables DA type configuration sysconfig screen and coverage selector',
    category: 'Short-term Rollout',
    enabled_by_default: false,
    editable_by_support: false,
    enabled_by_default_for_demo: false,
  },
  DELEGATED_AUTHORITY: {
    name: 'delegated_authority',
    display_name: 'Delegated Authority',
    description:
      'Enables Delegated Authority — named authority levels and condition-based override rules in System Settings.',
    category: 'Short-term Rollout',
    enabled_by_default: false,
    editable_by_support: true,
    enabled_by_default_for_demo: false,
  },
  CLIENT_SPECIFIC_FLOWS: {
    name: 'client_specific_flows',
    display_name: 'Clive - Client-Specific Flows',
    description:
      'Allow triggering client-specific AI flows through the internal trigger API for this organization (used by our PS team). Off refuses the trigger with 403.',
    category: 'Short-term Rollout',
    enabled_by_default: false,
    editable_by_support: false,
    enabled_by_default_for_demo: false,
  },
  EXTERNAL_QUESTIONS_API: {
    name: 'external_questions_api',
    display_name: 'External Insights API',
    description:
      'Rollout flag: allow API-key callers to use the external Insights API. Independent of the external Chat API flag.',
    category: 'Short-term Rollout',
    enabled_by_default: false,
    editable_by_support: true,
    enabled_by_default_for_demo: false,
  },
  ENFORCE_RATE_LIMIT_COOKIE_MISMATCH_LOGOUT: {
    name: 'enforce_rate_limit_cookie_mismatch_logout',
    display_name: 'Enforce Rate-Limit Cookie Mismatch Logout',
    description:
      'Terminate the session (logout + 401) when an authenticated request presents a per-user rate-limit cookie that does not match the logged-in user. When off (default), mismatches are only logged and the cookie is corrected on the response without disrupting the request.',
    category: 'Short-term Rollout',
    enabled_by_default: false,
    editable_by_support: false,
    enabled_by_default_for_demo: false,
  },
  SLA_ENGINE: {
    name: 'sla_engine',
    display_name: 'SLA Engine',
    description:
      'Enables the SLA engine: the SLA rules configuration tab, the daily org-local SLA evaluation job, and SLA configuration export/import. Off: the SLA tables stay inert, no rule can be authored and no evaluation runs.',
    category: 'Short-term Rollout',
    enabled_by_default: false,
    editable_by_support: false,
    enabled_by_default_for_demo: false,
  },
  GLOBAL_CONFIGURED_FIELDS: {
    name: 'global_configured_fields',
    display_name: 'Global Configured Fields',
    description: 'Projects organization-wide configured fields into every in-context FNOL and policy configuration.',
    category: 'Short-term Rollout',
    enabled_by_default: false,
    editable_by_support: true,
    enabled_by_default_for_demo: false,
  },
  HIERARCHY_FIELD_GROUPS: {
    name: 'hierarchy_field_groups',
    display_name: 'Hierarchy Field Groups',
    description:
      'Enables configuring an Additional Field Group of type Hierarchy (API), backed by an external endpoint or a pasted static tree, and serving its validated tree.',
    category: 'Short-term Rollout',
    enabled_by_default: false,
    editable_by_support: true,
    enabled_by_default_for_demo: false,
  },
  CONDITIONAL_TEMPLATE_SECTIONS: {
    name: 'conditional_template_sections',
    display_name: 'Conditional Template Sections',
    description:
      'Enables marking a block of an email, note or notification template body as shown only when a rule-builder condition on the claim, policy or exposure matches, and evaluating those conditions when the template renders.',
    category: 'Short-term Rollout',
    enabled_by_default: false,
    editable_by_support: true,
    enabled_by_default_for_demo: false,
  },
  AI_POLICY_PARSING: {
    name: 'ai_policy_parsing',
    display_name: 'AI Policy Parsing',
    description:
      "Enables the AI service to detect policy declarations pages among a claim's documents and extract the policy they describe for the claim.",
    category: 'Short-term Rollout',
    enabled_by_default: false,
    editable_by_support: true,
    enabled_by_default_for_demo: false,
  },
} as const;

export default CONFIGURATION_FEATURES;
